import { type DisplayAdsContext, DisplayAdsManager } from '@tunein/display-ads';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { api } from 'src/client/api';
import { player } from 'src/client/player';
import { buildConsentData } from 'src/client/player/utils';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { defaultLanguageCode } from 'src/common/constants/locale';
import { selectFirstVisitStartTime, selectOneTrust } from 'src/common/selectors/app';
import { PLATFORM_ENV_SHORTHAND } from 'src/common/utils/environment';

type DisplayAdsInitOptions = {
  locale?: string;
};

function getDisplayAdsContext(locale?: string): DisplayAdsContext {
  return {
    firstVisitTimestampMs: selectFirstVisitStartTime(storeSingleton.state),
    custom: {
      env: PLATFORM_ENV_SHORTHAND,
      language: (locale || defaultLanguageCode).substring(0, 2),
    },
  };
}

export class DisplayAdsSingleton extends Singleton<DisplayAdsManager> {
  #unsubscribeStore?: () => void;

  init(options: DisplayAdsInitOptions = {}) {
    if (this.isReady) {
      return true;
    }

    if (api.isPremium) {
      return false;
    }

    try {
      this.instance = new DisplayAdsManager({
        api: api.instance,
        playerManager: player,
        context: getDisplayAdsContext(options.locale),
      });
    } catch (error) {
      api.telemetry.logError({
        message: 'Display Ads Init Failed',
        context: {
          error,
        },
      });

      return false;
    }

    this.#setConsentData();
    this.#setupListeners();

    return true;
  }

  async setContext(context: DisplayAdsContext) {
    await this.ready;

    this.instance.setContext(context);
  }

  #setConsentData() {
    buildConsentData()
      .then((consentData) => {
        if (this.isReady) {
          this.instance.setConsent(consentData);
        }
      })
      .catch((error) => {
        api.telemetry.logError({
          message: 'Display Ads Consent Update Failed',
          context: {
            error,
          },
        });
      });
  }

  #setupListeners() {
    if (this.#unsubscribeStore) {
      return;
    }

    let oneTrust = selectOneTrust(storeSingleton.state);

    this.#unsubscribeStore = storeSingleton.subscribe(() => {
      const previousOneTrust = oneTrust;
      oneTrust = selectOneTrust(storeSingleton.state);

      if (previousOneTrust !== oneTrust) {
        this.#setConsentData();
      }
    });
  }
}

export const displayAds = new DisplayAdsSingleton() as SingletonInstance<
  DisplayAdsManager,
  DisplayAdsSingleton
>;
